import { useInfiniteQuery } from '@tanstack/react-query';

import { useLibraryPosts } from '../../hooks/useLibraryPosts';
import type { LibraryPostSummary } from '../../model/types';
import { Post } from '../list/Community';

import { TabRootFlatList } from '@/components/TabRootList';
import { EmptyQueryResults } from '@/components/emptyQueryResults';
import { Divider } from '@/components/ui/divider';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useToaster } from '@/hooks/useToaster';

export type CommunitySavedPostsSceneProps = {
  onViewAllPosts: () => void;
};

/**
 * Saved-posts tab for native Library. Unlike Inquiries/Content Posts, this
 * scene has no web equivalent route — the saved-posts view on web still
 * lives inside `Community`, reachable via the All | Saved tabs in `Community`.
 */
export function CommunitySavedPostsScene({ onViewAllPosts }: CommunitySavedPostsSceneProps) {
  const { isUnauthenticated, needsNpiVerification } = useAuthStatus();

  const blocked = isUnauthenticated || needsNpiVerification;

  const { requestAccess } = useAbilityGate();

  const {
    data: savedPostsData,
    refetch: savedPostsRefetch,
    fetchNextPage: savedPostsFetchNextPage,
    hasNextPage: savedPostsHasNextPage,
    isFetchingNextPage: savedPostsIsFetchingNextPage,
    isLoading: savedPostsIsLoading,
    isError: savedPostsIsError,
  } = useLibraryPosts({ saved: true });

  const savedPosts = (savedPostsData?.pages?.flatMap?.((page) => (page?.posts ? page.posts : [])) ??
    []) as LibraryPostSummary[];

  const toast = useToaster();

  if (savedPostsIsError) {
    toast.showError({
      title: 'There was an error loading your saved inquiries. Please try again.',
    });
  }

  return (
    <TabRootFlatList
      className="w-full"
      keyboardDismissMode="on-drag"
      showsVerticalScrollIndicator={false}
      data={savedPosts}
      keyExtractor={(_, index) => index.toString()}
      renderItem={({ item, index }) => (
        <Post
          item={item}
          searchInput=""
          sortBy="newest"
          saved
          position={index + 1}
          blocked={blocked}
          onBlockedPress={requestAccess}
        />
      )}
      ItemSeparatorComponent={() => <Divider />}
      onEndReachedThreshold={0.8}
      onEndReached={() =>
        !isUnauthenticated && !needsNpiVerification && savedPostsHasNextPage
          ? savedPostsFetchNextPage()
          : null
      }
      ListEmptyComponent={
        savedPostsIsLoading ? null : (
          <EmptyQueryResults
            title="Nothing to see here!"
            text="When you save an inquiry, you can refer back to it here"
            cta="View inquiries"
            onCtaPress={onViewAllPosts}
          />
        )
      }
      ListFooterComponent={
        <VStack className="mb-8 px-4 lg:px-6">
          {savedPostsIsFetchingNextPage ? <Spinner size="large" /> : null}
        </VStack>
      }
      refreshControl={
        <RefreshControl
          refreshing={savedPostsIsFetchingNextPage}
          onRefresh={() => {
            savedPostsRefetch();
          }}
        />
      }
    />
  );
}
